import React from 'react';
import { useTranslation } from './i18n.js';
import { getSupportedLanguage, supportedTranslationLanguages } from './detect.js';
import { CollapsibleCard } from './collapsible.js';
import { AudioPlayer } from './audioPlayer.js';
import { speechLocaleFor } from './speechLanguages.js';
import { apiUrl } from './config.js';
import { SubscriptionsLinkRequest } from './subscriptionsLink.js';

function createMathChallenge() {
  const a = Math.floor(Math.random() * 8) + 1;
  const b = Math.floor(Math.random() * 8) + 1;
  return {
    question: `${a} + ${b}`,
    answer: String(a + b),
  };
}

export function ThoughtCard({ initialData }) {
  const { t } = useTranslation();
  const [thought, setThought] = React.useState(initialData ? initialData.thought : null);
  const [languages, setLanguages] = React.useState(initialData ? initialData.languages : []);
  const normalizeLangCode = (value) => String(value || '').toLowerCase();
  const [selectedLang, setSelectedLang] = React.useState(() => {
    if (initialData) {
      return normalizeLangCode(initialData.selectedLang);
    }

    if (typeof window === 'undefined') {
      return 'en';
    }

    if (window.overrideLanguage) {
      return normalizeLangCode(window.overrideLanguage);
    }

    const stored = window.localStorage.getItem('thought-language');
    if (stored && supportedTranslationLanguages.includes(normalizeLangCode(stored))) {
      return normalizeLangCode(stored);
    }

    return normalizeLangCode(getSupportedLanguage());
  });
  const [loadingThought, setLoadingThought] = React.useState(!initialData);
  const [errorThought, setErrorThought] = React.useState('');
  const [subscribeEmail, setSubscribeEmail] = React.useState('');
  const [captchaValue, setCaptchaValue] = React.useState('');
  const [subscribeSuccess, setSubscribeSuccess] = React.useState(false);
  const [submitSubscribeError, setSubmitSubscribeError] = React.useState('');
  const [submittingSubscribe, setSubmittingSubscribe] = React.useState(false);
  const [formRenderTime] = React.useState(() => Date.now());
  const [honeypotValue, setHoneypotValue] = React.useState('');
  // `null` des deux côtés au premier rendu : générer le challenge (nombres aléatoires) pendant le
  // rendu casserait l'hydratation, le serveur et le client tirant chacun leur propre valeur —
  // uniquement vérifié côté client de toute façon (anti-spam), donc généré après coup, dans un
  // effet.
  const [mathChallenge, setMathChallenge] = React.useState(null);
  const [mathAnswer, setMathAnswer] = React.useState('');

  React.useEffect(() => {
    setMathChallenge(createMathChallenge());
  }, []);

  const languageFlags = {
    fr: '🇫🇷',
    en: '🇬🇧',
  };

  const getLanguageOptionLabel = (language) => language.name;

  const getLanguageIcon = (language) => {
    if (!language) {
      return languageFlags.en;
    }
    return language.flag || languageFlags[language.code] || '🏳️';
  };

  const fetchThought = (lang) => {
    setLoadingThought(true);
    setErrorThought('');

    const effectiveLang =
      lang ||
      window.overrideLanguage ||
      selectedLang ||
      getSupportedLanguage();
    const url = apiUrl('thought', { format: 'json', keyword: '0' });
    if (effectiveLang) {
      url.searchParams.set('language', effectiveLang);
    }
    if (window.distributor) {
      url.searchParams.set('distributor', window.distributor);
    }

    fetch(url.toString())
      .then((response) => {
        if (!response.ok) {
          throw new Error(t('thoughtSubscribeSubmitError'));
        }
        return response.json();
      })
      .then((data) => {
        setThought(data);

        if (Array.isArray(data.languages) && data.languages.length) {
          setLanguages(data.languages);

          const normalizedCodes = data.languages.map((language) => String(language.code || '').toLowerCase());
          const fallbackLang = normalizedCodes.includes('en') ? 'en' : normalizedCodes[0];
          const requestedLang = effectiveLang && normalizedCodes.includes(String(effectiveLang).toLowerCase())
            ? String(effectiveLang).toLowerCase()
            : fallbackLang;
          const actualLanguage = data.languages.find(
            (language) => String(language.code || '').toLowerCase() === requestedLang
          );
          const actualCode = actualLanguage ? String(actualLanguage.code || '').toLowerCase() : requestedLang;

          setSelectedLang(actualCode);
          window.localStorage.setItem('thought-language', actualCode);
        }
      })
      .catch((err) => {
        setErrorThought(err.message || t('thoughtErrorLoad'));
      })
      .finally(() => {
        setLoadingThought(false);
      });
  };

  React.useEffect(() => {
    if (initialData) {
      // Contenu déjà fourni par le rendu serveur pour `selectedLang` ; on ne refetch que
      // lors d'un changement de langue explicite (cf. handleLanguageChange).
      return;
    }
    fetchThought(selectedLang);
  }, []);

  // Appelé par le lecteur au premier clic sur lecture ; une nouvelle fonction par langue remet
  // le lecteur à zéro.
  const loadThoughtAudio = React.useCallback(async (signal) => {
    const response = await fetch(`/tts/thought?language=${encodeURIComponent(selectedLang)}`, { signal });
    if (response.status === 503) {
      throw new Error('unavailable');
    }
    if (!response.ok) {
      throw new Error('tts-request');
    }
    return URL.createObjectURL(await response.blob());
  }, [selectedLang]);

  const handleLanguageChange = (event) => {
    const nextLang = normalizeLangCode(event.target.value);
    setSelectedLang(nextLang);
    window.localStorage.setItem('thought-language', nextLang);
    fetchThought(nextLang);
  };

  const handleSubscribeEmailChange = (event) => {
    setSubscribeEmail(event.target.value);
    setSubmitSubscribeError('');
  };

  const handleCaptchaChange = (event) => {
    setCaptchaValue(event.target.value);
    setSubmitSubscribeError('');
  };

  const handleHoneypotChange = (event) => {
    setHoneypotValue(event.target.value);
  };

  const handleMathAnswerChange = (event) => {
    setMathAnswer(event.target.value);
    setSubmitSubscribeError('');
  };

  const handleSubscribeSubmit = (event) => {
    event.preventDefault();

    if (!thought || !thought.api_subscribe) {
      return;
    }

    const captchaData = thought && (() => {
      if (!thought) {
        return null;
      }
      if (typeof thought.captcha_url === 'string') {
        return {
          url: thought.captcha_url,
          fieldName: thought.captcha_field_name || thought.captchaFieldName || 'captcha',
        };
      }
      if (thought.captcha && typeof thought.captcha === 'object') {
        return {
          url: thought.captcha.image || thought.captcha.url || thought.captcha_url,
          fieldName: thought.captcha.field_name || thought.captcha.fieldName || 'captcha',
        };
      }
      return null;
    })();

    if (honeypotValue.trim()) {
      setSubmitSubscribeError(t('thoughtSubscribeSpamDetected'));
      return;
    }

    if (Date.now() - formRenderTime < 2000) {
      setSubmitSubscribeError(t('thoughtSubscribeTooFastError'));
      return;
    }

    if (!subscribeEmail.trim()) {
      setSubmitSubscribeError(t('thoughtSubscribeInvalidEmail'));
      return;
    }

    if (!mathAnswer.trim()) {
      setSubmitSubscribeError(t('thoughtSubscribeMathRequired'));
      return;
    }

    if (!mathChallenge || mathAnswer.trim() !== mathChallenge.answer) {
      setSubmitSubscribeError(t('thoughtSubscribeMathIncorrect'));
      return;
    }

    if (captchaData && !captchaValue.trim()) {
      setSubmitSubscribeError(t('thoughtCaptchaRequired'));
      return;
    }

    setSubmittingSubscribe(true);
    setSubmitSubscribeError('');

    const thoughtLanguage = thought.language || selectedLang || 'en';
    const languageCode = String(thoughtLanguage).slice(0, 2).toLowerCase();
    const params = new URLSearchParams({
      email: subscribeEmail.trim(),
      list: `prosveta_${languageCode}`,
      distributor: window.distributor || '',
      url: window.location.href,
    });

    // Diagnostic: log request details to help debug network / CORS / SSL issues
    try {
      console.debug('Thought subscribe - submitting', {
        url: thought.api_subscribe,
        body: params.toString(),
      });
    } catch (e) {
      // ignore logging errors
    }

    fetch(thought.api_subscribe, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: params.toString(),
    })
      .then((response) => {
        // Log response status for debugging (may be limited by CORS)
        try {
          console.debug('Thought subscribe - response', {
            status: response.status,
            statusText: response.statusText,
          });
        } catch (e) {}

        if (!response.ok) {
          throw new Error(t('thoughtSubscribeSubmitError'));
        }
        return response.json();
      })
      .then((result) => {
        const status = String((result && result.status) || result || '').trim().toUpperCase();
        if (status !== 'SUBSCRIBE') {
          throw new Error(t('thoughtSubscribeSubmitError'));
        }
        setSubscribeSuccess(true);
        setSubscribeEmail('');
        setMathAnswer('');
      })
      .catch((err) => {
        // Enhanced diagnostic logging
        try {
          console.error('Thought subscribe - error', err);
          console.debug('navigator.onLine =', typeof navigator !== 'undefined' ? navigator.onLine : 'unknown');
        } catch (e) {}

        const errorMessage = err && err.message ? err.message : '';
        if (err && (err.name === 'TypeError' || /failed to fetch/i.test(errorMessage) || /networkerror/i.test(errorMessage) || /ERR_FAILED/i.test(errorMessage))) {
          // Likely a network / CORS / SSL issue
          setSubmitSubscribeError(t('thoughtSubscribeNetworkError'));
        } else {
          setSubmitSubscribeError(errorMessage || t('thoughtSubscribeSubmitError'));
        }
      })
      .finally(() => {
        setSubmittingSubscribe(false);
      });
  };

  if (loadingThought) {
    return <div className="thought-card thought-card--loading" id="thought">{t('thoughtLoading')}</div>;
  }

  if (errorThought) {
    return (
      <div className="thought-card thought-card--error" id="thought">
        <strong>{t('thoughtErrorPrefix')}</strong> {errorThought}
      </div>
    );
  }

  if (!thought) {
    return null;
  }

  const { title, sentence, text, author } = thought;

  const currentLanguage = languages.find(
    (language) => language.code && String(language.code).toLowerCase() === String(selectedLang).toLowerCase()
  );
  const textDirection = (currentLanguage && currentLanguage.direction) || 'ltr';

  return (
    <CollapsibleCard as="article" id="thought" className="thought-card" dir={textDirection} label={t('editoTocThought')}>
      {languages.length > 0 && (
        <div className="thought-language-select">
          <select id="thought-language" value={selectedLang} onChange={handleLanguageChange}>
            {languages.map((language) => {
              const langCode = normalizeLangCode(language.code);
              return (
                <option key={langCode} value={langCode} data-flag={language.flag || ''}>
                  {language.name}
                </option>
              );
            })}
          </select>
        </div>
      )}
      {speechLocaleFor(selectedLang) && <AudioPlayer loadSrc={loadThoughtAudio} />}
      {title && <h2>{title}</h2>}
      {sentence && <p className="thought-card__sentence">{sentence}</p>}
      {text && <p className="thought-card__text">{text}</p>}
      {author && <p className="thought-card__author">{author}</p>}

      <section className="thought-subscribe" aria-labelledby="thought-subscribe-title">
        {!subscribeSuccess && <h3 id="thought-subscribe-title">{t('thoughtSubscribeTitle')}</h3>}
        {subscribeSuccess ? (
          <p className="thought-subscribe-message thought-subscribe-message--info"
            dangerouslySetInnerHTML={{ __html: t('thoughtSubscribeConfirmEmail') }}
          />
        ) : (
          <form className="thought-subscribe-form" onSubmit={handleSubscribeSubmit} noValidate>
            {submitSubscribeError && (
              <p className="thought-subscribe-message thought-subscribe-message--error">
                {submitSubscribeError}
              </p>
            )}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="thought-subscribe-honey-pot">Ne remplissez pas ce champ</label>
              <input
                id="thought-subscribe-honey-pot"
                name="honey_pot"
                type="text"
                value={honeypotValue}
                onChange={handleHoneypotChange}
                autoComplete="off"
                tabIndex="-1"
              />
            </div>
            <input
              id="thought-subscribe-email"
              type="email"
              aria-label={t('thoughtSubscribeEmailPlaceholder')}
              value={subscribeEmail}
              onChange={handleSubscribeEmailChange}
              placeholder={t('thoughtSubscribeEmailPlaceholder')}
              required
            />
            {mathChallenge && (
              <div className="thought-math-challenge">
                <label htmlFor="thought-subscribe-math-answer">
                  {t('thoughtSubscribeMathQuestion')} {mathChallenge.question}
                </label>
                <input
                  id="thought-subscribe-math-answer"
                  type="text"
                  value={mathAnswer}
                  onChange={handleMathAnswerChange}
                  placeholder={t('thoughtSubscribeMathPlaceholder')}
                  required
                />
              </div>
            )}
            {thought && (thought.captcha_url || (thought.captcha && typeof thought.captcha === 'object')) && (
              <div className="thought-captcha-block">
                {thought.captcha_url && (
                  <img
                    className="thought-captcha-image"
                    src={thought.captcha_url}
                    alt={t('thoughtCaptchaImageAlt')}
                  />
                )}
                <label htmlFor="thought-captcha-input">{t('thoughtCaptchaLabel')}</label>
                <input
                  id="thought-captcha-input"
                  type="text"
                  value={captchaValue}
                  onChange={handleCaptchaChange}
                  placeholder={t('thoughtCaptchaPlaceholder')}
                  required
                />
              </div>
            )}
            <button type="submit" disabled={submittingSubscribe}>
              {submittingSubscribe ? t('thoughtSubscribeSending') : t('thoughtSubscribeButton')}
            </button>
          </form>
        )}
        <SubscriptionsLinkRequest />
      </section>
    </CollapsibleCard>
  );
}
